import { useState, useEffect } from 'react';
import TopicPicker from './components/TopicPicker';
import TopicQuiz from './components/TopicQuiz';
import Tutor from './components/Tutor';
import Progress from './components/Progress';
import UpgradeScreen from './components/UpgradeScreen';
import MockExam from './components/MockExam';
import ExamResults from './components/ExamResults';
import AuthScreen from './components/AuthScreen';
import AdBanner from './components/AdBanner';
import Leaderboard from './components/Leaderboard';
import EssayPractice from './components/EssayPractice';
import Onboarding from './components/Onboarding';
import { SUBJECTS, TOPICS, SUBJECT_COLORS, SUBJECT_GRADIENTS, SUBJECT_EMOJIS } from './data';
import { useFreemium } from './hooks/useFreemium';
import { useProgress } from './hooks/useProgress';
import useStreak from './hooks/useStreak';
import { LanguageProvider, useLang } from './context/LanguageContext';
import supabase from './lib/supabase';
import './App.css';

function ReminderButton() {
  const { t } = useLang();
  const [status, setStatus] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );

  async function handleEnable() {
    if (!('Notification' in window)) return;
    const result = await Notification.requestPermission();
    setStatus(result);
    if (result === 'granted') {
      new Notification('GEDReady Prep', {
        body: "You're all set! We'll remind you to study every day. 📚",
        icon: '/logo192.png',
      });
    }
  }

  if (status === 'granted') return <p className="reminder-on">{t('remindersOn')}</p>;
  if (status === 'unsupported' || status === 'denied') return null;
  return <button className="reminder-btn" onClick={handleEnable}>{t('enableReminders')}</button>;
}

function ResetPasswordScreen({ onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) { setError(error.message); setLoading(false); return; }
    setDone(true);
    setTimeout(onDone, 1500);
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-logo">
          <span className="sidebar-logo">GED</span>
          <span className="sidebar-logo-sub">Ready Prep</span>
        </div>
        <h2 style={{ color: '#e2e8f0', marginBottom: 8 }}>Set New Password</h2>
        {done ? (
          <p style={{ color: '#34d399', textAlign: 'center' }}>✓ Password updated! Redirecting…</p>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit}>
            <input className="auth-input" type="password" placeholder="New password"
              value={password} onChange={e => setPassword(e.target.value)} required minLength={6} />
            <input className="auth-input" type="password" placeholder="Confirm new password"
              value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={6} />
            {error && <p className="auth-error">{error}</p>}
            <button className="primary-btn auth-submit"
              style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }} disabled={loading}>
              {loading ? 'Saving…' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Main App — state only, renders LanguageProvider > AppContent ──
function App() {
  const [screen, setScreen] = useState('home');
  const [subject, setSubject] = useState(null);
  const [topic, setTopic] = useState(null);
  const [tutorContext, setTutorContext] = useState(null);
  const [examResults, setExamResults] = useState(null);
  const [examKey, setExamKey] = useState(0);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const freemium = useFreemium(user);
  const progress = useProgress(user);
  const streak = useStreak(user);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const access_token = params.get('access_token');
      const refresh_token = params.get('refresh_token');
      const type = params.get('type');
      if (access_token && refresh_token) {
        supabase.auth.setSession({ access_token, refresh_token }).then(({ data: { session } }) => {
          if (session) {
            setUser(session.user);
            setAuthLoading(false);
            window.history.replaceState({}, '', window.location.pathname);
            if (type === 'recovery') setScreen('resetPassword');
          }
        });
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') === 'true') {
      setBanner('upgraded');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('cancelled') === 'true') {
      setBanner('cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user && !localStorage.getItem('gedready-onboarded')) {
      setShowOnboarding(true);
    }
  }, [user]);

  if (authLoading) return <div className="auth-loading"><div className="exam-spinner" style={{ borderTopColor: '#6366f1' }} /></div>;
  if (!user) return <LanguageProvider user={null}><AuthScreen /></LanguageProvider>;

  return (
    <LanguageProvider user={user}>
      <AppContent
        user={user}
        screen={screen} setScreen={setScreen}
        subject={subject} setSubject={setSubject}
        topic={topic} setTopic={setTopic}
        tutorContext={tutorContext} setTutorContext={setTutorContext}
        examResults={examResults} setExamResults={setExamResults}
        examKey={examKey} setExamKey={setExamKey}
        banner={banner} setBanner={setBanner}
        menuOpen={menuOpen} setMenuOpen={setMenuOpen}
        showOnboarding={showOnboarding} setShowOnboarding={setShowOnboarding}
        freemium={freemium} progress={progress} streak={streak}
      />
    </LanguageProvider>
  );
}

// ── AppContent — all rendering, uses t() for translations ──
function AppContent({ user, screen, setScreen, subject, setSubject, topic, setTopic,
  tutorContext, setTutorContext, examResults, setExamResults, examKey, setExamKey,
  banner, setBanner, menuOpen, setMenuOpen, showOnboarding, setShowOnboarding,
  freemium, progress, streak }) {

  const { lang, switchLang, t, tSubject, tTopic } = useLang();
  const color = subject ? SUBJECT_COLORS[subject] : '#1a73e8';

  function goHome() { setScreen('home'); setSubject(null); setTopic(null); setTutorContext(null); }
  function selectSubject(s) { setSubject(s); setScreen('subject'); }
  function selectTopic(tp) { setTopic(tp); setScreen('quiz'); }
  function handleAskAI(question, subj, top) { setTutorContext({ question, subject: subj, topic: top }); setScreen('tutor'); }
  function handleBack() {
    if (screen === 'quiz') { setScreen('subject'); setTopic(null); return; }
    if (screen === 'subject') { goHome(); return; }
    if (screen === 'tutor' && tutorContext) { setScreen('quiz'); return; }
    goHome();
  }
  function closeMenu() { setMenuOpen(false); }

  const headerTitle =
    screen === 'home' ? t('chooseSubject') :
    screen === 'subject' ? subject :
    screen === 'quiz' ? topic :
    screen === 'tutor' ? t('aiTutor') :
    screen === 'progress' ? t('yourProgress') :
    screen === 'upgrade' ? t('goPremium') :
    screen === 'exam' ? t('mockGEDExam') :
    screen === 'examresults' ? t('examResults') :
    screen === 'essay' ? t('essayPractice') : '';

  const showBack = screen !== 'home' && screen !== 'progress' && screen !== 'upgrade' && screen !== 'exam' && screen !== 'examresults';

  return (
    <div className="app-layout">

      {showOnboarding && <Onboarding onDone={() => setShowOnboarding(false)} lang={lang} t={t} />}

      {menuOpen && <div className="sidebar-backdrop" onClick={closeMenu} />}

      {/* ── Sidebar ── */}
      <aside className={`sidebar ${menuOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar-brand">
          <div className="sidebar-brand-row">
            <div>
              <span className="sidebar-logo">GED</span>
              <span className="sidebar-logo-sub">Ready Prep</span>
              <span className="sidebar-tagline">{lang === 'es' ? 'Tu camino al éxito en el GED' : 'Your path to GED success'}</span>
            </div>
            <button className="sidebar-close" onClick={closeMenu}>✕</button>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button className={`sidebar-item ${['home','subject','quiz'].includes(screen) ? 'sidebar-item--active' : ''}`}
            onClick={() => { goHome(); closeMenu(); }}>
            <span className="sidebar-icon">📚</span> {t('study')}
          </button>
          <button className={`sidebar-item ${screen === 'progress' ? 'sidebar-item--active' : ''}`}
            onClick={() => { setScreen('progress'); closeMenu(); }}>
            <span className="sidebar-icon">📊</span> {t('progress')}
          </button>
          <button className={`sidebar-item ${['exam','examresults'].includes(screen) ? 'sidebar-item--active' : ''}`}
            onClick={() => { setExamResults(null); setExamKey(k => k + 1); setScreen('exam'); closeMenu(); }}>
            <span className="sidebar-icon">📝</span> {t('mockExam')}
            {!freemium.isPremium && <span className="sidebar-pro-tag">PRO</span>}
          </button>
          <button className={`sidebar-item ${screen === 'tutor' ? 'sidebar-item--active' : ''}`}
            onClick={() => { if (!freemium.isPremium) { setScreen('upgrade'); } else { setTutorContext(null); setScreen('tutor'); } closeMenu(); }}>
            <span className="sidebar-icon">🤖</span> {t('aiTutor')}
            {!freemium.isPremium && <span className="sidebar-pro-tag">PRO</span>}
          </button>
          <button className={`sidebar-item ${screen === 'essay' ? 'sidebar-item--active' : ''}`}
            onClick={() => { if (!freemium.isPremium) { setScreen('upgrade'); } else { setScreen('essay'); } closeMenu(); }}>
            <span className="sidebar-icon">✍️</span> {t('essayPractice')}
            {!freemium.isPremium && <span className="sidebar-pro-tag">PRO</span>}
          </button>
          <button className={`sidebar-item ${screen === 'leaderboard' ? 'sidebar-item--active' : ''}`}
            onClick={() => { setScreen('leaderboard'); closeMenu(); }}>
            <span className="sidebar-icon">🏆</span> {t('leaderboard')}
          </button>
          {!freemium.isPremium && (
            <button className={`sidebar-item ${screen === 'upgrade' ? 'sidebar-item--active' : ''}`}
              onClick={() => { setScreen('upgrade'); closeMenu(); }}>
              <span className="sidebar-icon">⭐</span> {t('upgrade')}
            </button>
          )}
        </nav>

        <div className="sidebar-footer">
          {freemium.isPremium ? (
            <div className="sidebar-premium">{t('premium')}</div>
          ) : (
            <div className="sidebar-quota">
              <div className="quota-label">{t('questionsLeft', freemium.questionsLeft)}</div>
              <div className="quota-bar">
                <div className="quota-fill" style={{ width: `${(freemium.questionsLeft / 5) * 100}%` }} />
              </div>
              <button className="quota-upgrade" onClick={() => setScreen('upgrade')}>{t('upgradeUnlimited')}</button>
            </div>
          )}
          {streak > 0 && <div className="sidebar-streak">{t('streak', streak)}</div>}

          {/* Language toggle */}
          <div className="lang-toggle">
            <button className={`lang-btn ${lang === 'en' ? 'lang-btn--active' : ''}`} onClick={() => switchLang('en')}>EN</button>
            <button className={`lang-btn ${lang === 'es' ? 'lang-btn--active' : ''}`} onClick={() => switchLang('es')}>ES</button>
          </div>

          <ReminderButton />
          <div className="sidebar-user">
            <span className="sidebar-user-email">{user.email}</span>
            <button className="sidebar-logout" onClick={() => supabase.auth.signOut()}>{t('logOut')}</button>
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="app-main">
        {banner && (
          <div className={`stripe-banner stripe-banner--${banner}`}>
            {banner === 'upgraded'
              ? (lang === 'es' ? '🎉 ¡Bienvenido a Premium! Tu cuenta ha sido mejorada.' : '🎉 Welcome to Premium! Your account has been upgraded.')
              : (lang === 'es' ? 'Pago cancelado — puedes mejorar cuando quieras.' : 'Payment cancelled — you can upgrade anytime.')}
            <button className="stripe-banner-close" onClick={() => setBanner(null)}>✕</button>
          </div>
        )}

        <header className="app-header" style={{ background: 'rgba(13,11,30,0.75)', backdropFilter: 'blur(16px)', borderBottom: `1px solid ${color}50` }}>
          <button className="hamburger" onClick={() => setMenuOpen(o => !o)}>☰</button>
          {showBack ? <button className="back-btn" onClick={handleBack}>{t('back')}</button> : <div className="header-spacer" />}
          <h1 className="app-title">{headerTitle}</h1>
          <div className="header-spacer" />
        </header>

        {screen === 'home' && (
          <main className="main-content">
            <p className="page-intro">{t('pageIntro')}</p>
            <div className="subject-grid">
              {SUBJECTS.map(s => {
                const total = Object.entries(progress.progress)
                  .filter(([k]) => TOPICS[s].includes(k))
                  .reduce((sum, [, v]) => sum + v.total, 0);
                return (
                  <button key={s} className="subject-card" style={{ background: SUBJECT_GRADIENTS[s] }} onClick={() => selectSubject(s)}>
                    <span className="subject-emoji">{SUBJECT_EMOJIS[s]}</span>
                    <span className="subject-name">{tSubject(s)}</span>
                    <span className="subject-meta">{t('topics', TOPICS[s].length)}</span>
                    {total > 0 && <span className="subject-answered">{t('answered', total)}</span>}
                  </button>
                );
              })}
            </div>
          </main>
        )}

        {screen === 'subject' && (
          <main className="main-content">
            <TopicPicker subject={subject} topics={TOPICS[subject]} color={color}
              getTopicAccuracy={progress.getTopicAccuracy} isPremium={freemium.isPremium}
              questionsLeft={freemium.questionsLeft} onSelect={selectTopic}
              onUpgrade={() => setScreen('upgrade')} t={t} tTopic={tTopic} />
          </main>
        )}

        {screen === 'quiz' && (
          <main className="main-content">
            <TopicQuiz subject={subject} topic={topic} color={color}
              consumeQuestion={freemium.consumeQuestion} onAskAI={handleAskAI}
              onRecordAnswer={progress.recordAnswer} onFinish={() => setScreen('subject')}
              onUpgrade={() => setScreen('upgrade')} t={t} lang={lang} />
          </main>
        )}

        {screen === 'tutor' && <Tutor color={color} context={tutorContext} t={t} lang={lang} />}

        {screen === 'progress' && (
          <main className="main-content">
            <Progress getTopicAccuracy={progress.getTopicAccuracy}
              getTotalQuestions={progress.getTotalQuestions}
              getReadinessScore={progress.getReadinessScore} t={t} />
          </main>
        )}

        {screen === 'upgrade' && (
          <main className="main-content">
            <UpgradeScreen onBack={handleBack} t={t} />
          </main>
        )}

        <main className="main-content" style={{ display: screen === 'exam' ? 'block' : 'none' }}>
          <MockExam key={examKey} user={user} isPremium={freemium.isPremium}
            onUpgrade={() => setScreen('upgrade')} t={t} lang={lang} tSubject={tSubject}
            onComplete={async (results) => {
              setExamResults(results);
              setScreen('examresults');
              const totalCorrect = Object.values(results.scores).reduce((s, sc) => s + (sc.correct || 0), 0);
              const totalQ = Object.values(results.scores).reduce((s, sc) => s + (sc.total || 0), 0);
              const score = totalQ > 0 ? Math.round((totalCorrect / totalQ) * 100) : 0;
              if (user) await supabase.from('mock_exam_scores').insert({ user_id: user.id, email: user.email, score });
            }} />
        </main>

        {screen === 'essay' && (
          <main className="main-content">
            <EssayPractice t={t} lang={lang} />
          </main>
        )}

        {screen === 'leaderboard' && (
          <main className="main-content"><Leaderboard /></main>
        )}

        {screen === 'examresults' && examResults && (
          <main className="main-content">
            <ExamResults results={examResults} onStudy={goHome} t={t} />
          </main>
        )}

        {screen === 'resetPassword' && (
          <main className="main-content"><ResetPasswordScreen onDone={goHome} /></main>
        )}

        {!freemium.isPremium && <AdBanner />}
      </div>
    </div>
  );
}

export default App;
